import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchOld } from 'hooks';
import Modal from 'utils/packages/Modal';
import { isWeb } from '../../../constants';
import { getDisputeToken } from '../actions';
import { DisputeItemPickerModal } from '../components/DisputeItemPickerModal';
import { buildDisputeFormHtml, getDisputeUrl } from '../utils/dispute';
import { getDisputeMeta } from '../utils/disputeMeta';
const strings = {
    errorTitle: 'Something went wrong',
    errorBody: 'We’re unable to link with TransUnion right now. Please check your internet connection or try again shortly',
    retry: 'Try again',
    close: 'Close',
};
const useCreditReportDispute = (items) => {
    const [tokenData, fetchError, isFetching, fetchToken] = useFetchOld();
    const isSingleItem = items.length === 1;
    const [disputeItem, setDisputeItem] = useState(() => isSingleItem ? items[0] : null);
    const [disputeError, setDisputeError] = useState(false);
    const [isWebViewReady, setIsWebViewReady] = useState(false);
    const loadDisputeToken = useCallback(() => {
        setDisputeError(false);
        setIsWebViewReady(false);
        fetchToken(getDisputeToken());
    }, [fetchToken]);
    const onDisputeError = useCallback(() => {
        setDisputeError(true);
    }, []);
    const onWebViewReady = useCallback(() => {
        setIsWebViewReady(true);
    }, []);
    useEffect(() => {
        if (isSingleItem && !disputeItem) {
            setDisputeItem(items[0]);
        }
    }, [isSingleItem, disputeItem, items]);
    useEffect(() => {
        if (isSingleItem) {
            loadDisputeToken();
        }
    }, [isSingleItem, loadDisputeToken, disputeItem]);
    const hasError = Boolean(fetchError) || disputeError;
    useEffect(() => {
        if (!hasError)
            return;
        Modal.showConfirmation({
            title: strings.errorTitle,
            subtitle: strings.errorBody,
            confirmButtonTitle: strings.retry,
            cancelButtonTitle: strings.close,
            onConfirm: loadDisputeToken,
        });
    }, [hasError, loadDisputeToken]);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const didSelectRef = useRef(false);
    const onPressPicker = useCallback(() => {
        setIsPickerOpen(true);
        didSelectRef.current = false;
        DisputeItemPickerModal.show({
            items,
            onSelect: (item) => {
                didSelectRef.current = true;
                setDisputeItem(item);
            },
            onWillClose: () => setIsPickerOpen(false),
            onAfterClose: () => {
                if (didSelectRef.current)
                    loadDisputeToken();
            },
        });
    }, [items, loadDisputeToken]);
    const selectedMeta = useMemo(() => {
        const source = disputeItem ?? items[0];
        if (!source)
            return null;
        return getDisputeMeta(source.disputeType, {
            iconUrl: source.iconUrl,
            lenderName: source.lenderName,
        });
    }, [disputeItem, items]);
    const formFields = useMemo(() => {
        if (!disputeItem || !tokenData?.token || !tokenData?.userIdentifier) {
            return null;
        }
        const all = {
            userIdentifier: tokenData.userIdentifier,
            disputeType: disputeItem.disputeType,
            useToken: 'true',
            token: tokenData.token,
        };
        Object.entries(disputeItem.fields).forEach(([name, value]) => {
            if (value === undefined || value === null || value === '')
                return;
            all[name] = String(value);
        });
        return all;
    }, [tokenData, disputeItem]);
    const html = useMemo(() => {
        if (isWeb || !formFields)
            return null;
        return buildDisputeFormHtml({
            url: getDisputeUrl(),
            fields: formFields,
        });
    }, [formFields]);
    return {
        disputeItem,
        selectedMeta,
        isFetching,
        hasError,
        formFields,
        html,
        isPickerOpen,
        isSingleItem,
        onPressPicker,
        onDisputeError,
        isWebViewReady,
        onWebViewReady,
    };
};
export default useCreditReportDispute;
